import React, { useContext } from 'react'
import { useParams } from 'react-router-dom'
import { FormattedMessage } from 'react-intl'

import ConfirmIcon from 'assets/icons/confirm-icon'
import RejectIcon from 'assets/icons/reject-icon'
import RemoveIcon from 'assets/icons/remove-icon'
import ViewSiteIcon from 'assets/icons/view-site-icon'
import ActionsMenu from 'features/ActionMenu'
import { confirmSpecies, requestUpdateSiteSpecie } from 'api/siteSpecie'
import { SITE_STATUS_NOMINATED_ID, SITE_STATUS_DRAFT_ID } from 'constants/siteStatuses'
import { canConfirmSites, canNotViewUpdateSitesData } from 'utils/rolePermissions'
import { ProfileContext } from 'providers/profile'
import Tooltip from 'components/Tooltip'
import Button from 'components/Button'
import CalendarIcon from 'assets/icons/calendar-icon'
import { SiteContext } from 'providers/site'
import { defaultSiteProposalValidationResults } from 'constants/defaultSiteProposalValidationResults'
import PulsingNotification from 'features/PulsingNotification'
import { reassessmentTriggerReasons } from 'constants/reassessmentTriggerReasons'
import ReassessIcon from 'assets/icons/reassess-icon'
import AssessIcon from 'assets/icons/assess-icon'

import {
  SpeciesConfirmDeclineListContainer,
  SpeciesConfirmDeclineListHalfSide,
  SpeciesConfirmDeclineListTitle,
  SpeciesConfirmDeclineListTitleIcon,
  SpeciesConfirmDeclineListTitleIconWrapper,
  SpeciesConfirmDeclineIcon,
  SpeciesLastReassessmentCalendarWrapper,
  SpeciesCalendarIconWrapper,
  SpeciesCalendarDivider,
  SpeciesCalendarDateText,
  SpeciesValidationIcon,
  SpeciesDeleteIconWrapper, SpeciesValidationIconWrapper, SpecieHeaderActionMenuWrapper
} from './style'
import { formatDate } from 'utils/dateFormat'

const formattedRejectIconTooltipHint = (item) => {
  return item.triggerReasonId.map((element) => {
    return reassessmentTriggerReasons.find((elementId) => {
      return elementId.id === element
    }).name
  })
}


const tooltipXOffset = 0
const tooltipYOffset = -16

const SpeciesConfirmDeclineList = ({
  item,
  permitDelete,
  lastItem,
  onClick,
  site,
  getSiteSpecies,
  openRemovalModal,
  nonQualified
}) => {
  const dateToShow = item.isReassessed ? formatDate(item.updatedAt) : formatDate(item.lastReassessmentDate)
  const params = useParams()
  const { profile } = useContext(ProfileContext)
  const { siteProposalFieldValidation, isEditable } = useContext(SiteContext)

  const handleSpeciesAction = (isApproved) => {
    confirmSpecies(item.id, params.siteHistoryId, isApproved).then((data) => {
      getSiteSpecies()
    })
  }

  const removeSpecie = () => {
    openRemovalModal(item)
  }

  const actionMenuItems = item.isApprovedBySecretariat ? [{
    icon: <RejectIcon />,
    title: 'rejectSpecies',
    onClick: () => {
      handleSpeciesAction({ isApproved: false })
    },
    isDestructive: true
  }] : [{
    icon: <ConfirmIcon />,
    title: 'confirmSpecies',
    onClick: () => {
      handleSpeciesAction({ isApproved: true })
    }
  }]

  async function handleClick() {
    if (
      !item.isReassessed &&
      site.SiteStatusId === SITE_STATUS_DRAFT_ID &&
      !canNotViewUpdateSitesData(profile.RoleId) &&
      site.reassessmentFlag
    ) {
      try {
        const data = await requestUpdateSiteSpecie(
          item.id,
          params.siteHistoryId,
          item.historyId,
          { reassessDateStatus: false }
        );
        onClick(data);
        getSiteSpecies(); // Refresh the species list after reassessment
      } catch (error) {
        console.error("Error updating reassessment status:", error);
      }
      return;
    }
    onClick(item);
  }


  const specieId = item.reassessmentFlag ? item.historyId : item.id

  const validationSpecies = siteProposalFieldValidation.species[specieId]
    ? siteProposalFieldValidation.species[specieId]
    : defaultSiteProposalValidationResults.species

  const parametersId = item.SpeciesAssesmentParameters.map((element) => {
    return element.id
  })

  // eslint-disable-next-line no-magic-numbers
  const parametersFields = siteProposalFieldValidation.species[specieId] ? parametersId.length > 0 ? parametersId.map((element) => {
    const parameter = item.SpeciesAssesmentParameters.find(p => p.id === element)
    const isIbaOnly = parameter?.iba_only === true
    const validationFields = siteProposalFieldValidation?.assessmentParameters[element]
    
    if (!validationFields) {
      return []
    }
    
    // When iba_only is true, exclude bestGlobal, sourceGlobalData, and natureOfOccurence from validation count
    if (isIbaOnly) {
      const filteredFields = { ...validationFields }
      // Remove these fields from validation count when iba_only is true
      delete filteredFields.bestGlobal
      delete filteredFields.sourceGlobalData
      delete filteredFields.natureOfOccurence
      return Object.values(filteredFields)
    }
    
    return Object.values(validationFields)
  }).flat() : [] : []

  const fieldsToCompleteNumber = !validationSpecies.triggerReasonId ? [1] : nonQualified ? [] : Object.values(validationSpecies).filter((element) => {
    return element === false
  }).concat(parametersFields.filter((value) => {
    return !value
  }))


  return (
    <SpeciesConfirmDeclineListContainer lastItem={lastItem} onClick={() => {}}>
      <SpeciesConfirmDeclineListHalfSide>
        <SpeciesConfirmDeclineListTitleIconWrapper>
          <SpeciesConfirmDeclineListTitleIcon isRejected={nonQualified} />
        </SpeciesConfirmDeclineListTitleIconWrapper>
        <SpeciesConfirmDeclineListTitle isRejected={nonQualified}>
          {item.scientificName}
        </SpeciesConfirmDeclineListTitle>
      </SpeciesConfirmDeclineListHalfSide>
      <SpeciesConfirmDeclineListHalfSide>
        {item.lastReassessmentDate ? (
          <>
            <Tooltip
              style={{ zIndex: 1000 }}
              placement='top'
              trigger={['hover']}
              align={{ offset: [tooltipXOffset, tooltipYOffset] }}
              overlay={{ label: 'lastTimeReassessed' }}
            >
              <SpeciesLastReassessmentCalendarWrapper>
                <SpeciesCalendarIconWrapper>
                  <CalendarIcon />
                </SpeciesCalendarIconWrapper>
                <SpeciesCalendarDateText>
                  {dateToShow}
                </SpeciesCalendarDateText>
                <SpeciesCalendarDivider />
              </SpeciesLastReassessmentCalendarWrapper>
            </Tooltip>
            <SpeciesConfirmDeclineIcon type={nonQualified ? 'rejected' : 'confirmed'}>
              {nonQualified ? (
                <Tooltip
                  style={{ zIndex: 1000 }}
                  placement='top'
                  trigger={['hover']}
                  align={{ offset: [tooltipXOffset, tooltipYOffset] }}
                  overlay={{ label: 'nonQualifying', infoTooltip: formattedRejectIconTooltipHint(item).join(', ') }}
                >
                  <div>
                    <RejectIcon />
                  </div>
                </Tooltip>
              ) : (
                <Tooltip
                  style={{ zIndex: 1000 }}
                  placement='top'
                  trigger={['hover']}
                  align={{ offset: [tooltipXOffset, tooltipYOffset] }}
                  overlay={{ label: 'qualifies' }}
                >
                  <div>
                    <ConfirmIcon />
                  </div>
                </Tooltip>
              )}
            </SpeciesConfirmDeclineIcon>
            <Button height='32px' marginTop='0' onClick={handleClick}>
              {site.SiteStatusId === SITE_STATUS_DRAFT_ID ? <ReassessIcon /> : <ViewSiteIcon />}
              <FormattedMessage id={site.SiteStatusId === SITE_STATUS_DRAFT_ID ? 'reassess' : 'view'} />
            </Button>
            {(permitDelete && isEditable) && (
              <SpeciesDeleteIconWrapper onClick={removeSpecie}>
                <RemoveIcon />
              </SpeciesDeleteIconWrapper>
            )}
            {(site.SiteStatusId === SITE_STATUS_NOMINATED_ID && canConfirmSites(profile.RoleId)) && (
              <SpecieHeaderActionMenuWrapper>
                <ActionsMenu table={false} dropdownItems={actionMenuItems} margin='0' />
              </SpecieHeaderActionMenuWrapper>
            )}
            {/* eslint-disable-next-line no-magic-numbers */}
            {fieldsToCompleteNumber.length > 0 && (
              <SpeciesValidationIconWrapper>
                <SpeciesValidationIcon>
                  <PulsingNotification number={fieldsToCompleteNumber.length} />
                </SpeciesValidationIcon>
              </SpeciesValidationIconWrapper>
            )}
          </>
        ) : (
          <>
            {site.reassessmentFlag && (
              <SpeciesConfirmDeclineIcon type={nonQualified ? 'rejected' : 'confirmed'}>
                {nonQualified ? (
                  <Tooltip
                    style={{ zIndex: 1000 }}
                    placement='top'
                    trigger={['hover']}
                    align={{ offset: [tooltipXOffset, tooltipYOffset] }}
                    overlay={{ label: 'nonQualifying', infoTooltip: formattedRejectIconTooltipHint(item).join(', ') }}
                  >
                    <div>
                      <RejectIcon />
                    </div>
                  </Tooltip>
                ) : (
                  <Tooltip
                    style={{ zIndex: 1000 }}
                    placement='top'
                    trigger={['hover']}
                    align={{ offset: [tooltipXOffset, tooltipYOffset] }}
                    overlay={{ label: 'qualifies' }}
                  >
                    <div>
                      <ConfirmIcon />
                    </div>
                  </Tooltip>
                )}
              </SpeciesConfirmDeclineIcon>
            )}
            <Button height='32px' marginTop='0' onClick={handleClick}>
              {site.SiteStatusId === SITE_STATUS_DRAFT_ID ? <AssessIcon /> : <ViewSiteIcon />}
              <FormattedMessage id={site.SiteStatusId === SITE_STATUS_DRAFT_ID ? 'assess' : 'view'} />
            </Button>
            {(site.SiteStatusId === SITE_STATUS_NOMINATED_ID && canConfirmSites(profile.RoleId)) && (
              <SpecieHeaderActionMenuWrapper>
                <ActionsMenu table={false} dropdownItems={actionMenuItems} margin='0' />
              </SpecieHeaderActionMenuWrapper>
            )}
            {(permitDelete && isEditable && site.SiteStatusId === SITE_STATUS_DRAFT_ID) && (
              <SpeciesDeleteIconWrapper onClick={removeSpecie}>
                <RemoveIcon />
              </SpeciesDeleteIconWrapper>
            )}
            {/* eslint-disable-next-line no-magic-numbers */}
            {fieldsToCompleteNumber.length > 0 && (
              <SpeciesValidationIconWrapper>
                <SpeciesValidationIcon>
                  <PulsingNotification number={fieldsToCompleteNumber.length} />
                </SpeciesValidationIcon>
              </SpeciesValidationIconWrapper>
            )}
          </>
        )}
      </SpeciesConfirmDeclineListHalfSide>
    </SpeciesConfirmDeclineListContainer>
  )
}

export default SpeciesConfirmDeclineList
